import { Link } from 'react-router-dom';
import { useWorkspaceObjectives } from '@/lib/workspace-objectives';
import { canEditWorkspaceObjective, type WorkspaceObjective } from '@shared/types/workspace-objectives';
import { TodayObjectives } from './TodayObjectives';

const OBJECTIVES_HREF = '/profil?tab=objectives';

const dueText = (goal: WorkspaceObjective) => goal.due_date
  ? `Échéance ${new Date(`${goal.due_date}T12:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}`
  : 'Sans échéance';

/**
 * « Objectifs » tab of Mon travail (15/10): measured objectives as small tiles
 * (exact count, weekly pace), then the others with their due date and, for who
 * may edit them, « Ajouter une mesure ». They are managed in Profil.
 */
export const WorkObjectivesTab = () => {
  const { objectives, measures, userId, isManager, loading, error } = useWorkspaceObjectives();
  if (loading) return null;
  if (error) return <p role="alert" className="py-8 text-sm text-red-700">{error}</p>;
  const active = objectives.filter((goal) =>
    goal.status === 'active' && (goal.scope === 'company' || goal.owner_user_id === userId)
  );
  if (active.length === 0) {
    return (
      <div className="border-t border-domino-cardBorder py-8 text-sm text-domino-inkSoft">
        <p>Aucun objectif en cours.</p>
        <Link to={OBJECTIVES_HREF} className="mt-2 inline-block text-domino-aubergine underline underline-offset-4">Définir un objectif</Link>
      </div>
    );
  }
  const unmeasured = active.filter((goal) => !measures[goal.id]);
  return (
    <div className="space-y-5">
      <TodayObjectives />
      {unmeasured.length > 0 && (
        <section aria-label="Sans mesure">
          <h2 className="flex items-baseline justify-between gap-3 py-1.5 text-sm font-semibold text-domino-ink">
            Sans mesure<span className="text-xs font-normal tabular-nums text-domino-inkSoft">{unmeasured.length}</span>
          </h2>
          <ul className="mt-1 divide-y divide-domino-cardBorder rounded-lg border border-domino-cardBorder bg-domino-white">
            {unmeasured.map((goal) => (
              <li key={goal.id} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 px-3.5 py-2.5">
                <Link to={OBJECTIVES_HREF} className="min-w-0 text-sm text-domino-ink hover:text-domino-aubergine">{goal.title}</Link>
                <span className="text-xs text-domino-inkSoft">
                  {dueText(goal)}
                  {canEditWorkspaceObjective(goal, userId, isManager) && (
                    <> · <Link
                      to={`${OBJECTIVES_HREF}&objective=${encodeURIComponent(goal.id)}`}
                      className="text-domino-aubergine underline underline-offset-2"
                    >Ajouter une mesure</Link></>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};
